import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { getMyProfile, updateMyProfile, getPhotoUploadUrl, setPhotoUrl } from '../services/internApi';
import { uploadProfilePhoto } from '../utils/imageProcessor';
import StatusChip from '../components/common/StatusChip';
import VideoUploadCard from '../components/VideoUploadCard';

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUrl, setPhotoUrlState] = useState('');
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  // Editable fields
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [transportation, setTransportation] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(75);

  useEffect(() => {
    getMyProfile()
      .then((p) => {
        setProfile(p);
        setPhone(p.phone || '');
        setBio(p.bio || '');
        setTransportation(p.transportation || '');
        setEmergencyContact(p.emergency_contact || '');
        setRadiusMiles(Number(p.radius_miles) || 75);
        setPhotoUrlState(p.photo_url || '');
      })
      .catch((err) => setError(err?.response?.data?.error || 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const newUrl = await uploadProfilePhoto(file, getPhotoUploadUrl, setPhotoUrl);
      setPhotoUrlState(newUrl);
      setSnack({ open: true, message: 'Profile photo updated', severity: 'success' });
    } catch (err) {
      setSnack({
        open: true,
        message: err.message || 'Failed to upload photo',
        severity: 'error',
      });
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateMyProfile({
        phone,
        bio,
        transportation,
        emergency_contact: emergencyContact,
        radius_miles: Number(radiusMiles),
      });
      setSnack({ open: true, message: 'Profile updated', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!profile) return null;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>My Profile</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your account information and editable preferences.
      </Typography>

      {/* Onboarding intro-video upload — the post-test "Continue to Video
          Upload" button lands here. Hidden once past the video stage. */}
      <VideoUploadCard />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Account</Typography>

            {/* Profile photo with upload */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={photoUrl || undefined}
                  alt={profile.name}
                  sx={{
                    width: 100,
                    height: 100,
                    fontSize: 36,
                    bgcolor: 'primary.light',
                    border: '3px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  {(profile.name || '?').slice(0, 1).toUpperCase()}
                </Avatar>
                {uploadingPhoto && (
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '50%',
                  }}>
                    <CircularProgress size={32} sx={{ color: 'white' }} />
                  </Box>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{profile.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Auto-cropped to 400×400 and optimized.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                >
                  {photoUrl ? 'Change Photo' : 'Upload Photo'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Field label="Name" value={profile.name} />
              <Field label="Email" value={profile.email} />
              <Field label="College" value={profile.college_name || profile.college_id || '—'} />
              <Field label="Year" value={profile.year || '—'} />
              <Field label="GPA" value={profile.gpa || '—'} />
              <Field label="Status" value={<StatusChip status={profile.status} type="intern" />} />
              <Field label="Referral Code" value={profile.referral_code || '—'} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Editable Info</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Transportation"
                value={transportation}
                onChange={(e) => setTransportation(e.target.value)}
                fullWidth
                size="small"
                placeholder="Car, rideshare, transit, etc."
              />
              <TextField
                label="Emergency Contact"
                value={emergencyContact}
                onChange={(e) => setEmergencyContact(e.target.value)}
                fullWidth
                size="small"
                placeholder="Name + phone"
              />
              <TextField
                label="Service Radius (miles)"
                type="number"
                value={radiusMiles}
                onChange={(e) => setRadiusMiles(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 5, max: 250, step: 5 }}
                helperText="Only show jobs within this many miles of your ZIP. Default 75. Range: 5-250."
              />
              <TextField
                label="Short Bio (visible to clients)"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                fullWidth
                multiline
                rows={4}
                size="small"
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function Field({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}</Typography>
      <Typography variant="body2" component="div">{value}</Typography>
    </Box>
  );
}
